import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

const toLocalIST = (iso) => (iso ? new Date(new Date(iso).getTime() + 330 * 60000).toISOString().slice(0, 16) : "");
const STATUS_CLS = { live: "text-green-700", scheduled: "text-accent", hidden: "text-red-600" };

function ImportBox({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const run = async (files) => {
    setBusy(true);
    const out = [];
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try {
        const r = (await api.post("/admin/learn/import", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
        out.push({ file: f.name, ...r });
      } catch (e) {
        out.push({ file: f.name, fatal: e?.response?.data?.detail || "Upload failed", items: [] });
      }
    }
    setRows(out); setBusy(false); onDone();
  };
  return (
    <div className="rounded-lg border border-border bg-background p-5" data-testid="learn-import">
      <h3 className="font-heading font-bold text-ink">Import bundle</h3>
      <p className="mt-1 text-sm text-muted">.zip with manifest.json + images/PDFs, max 9 MB each. Pick several zips at once. Re-importing the same external_id updates it.</p>
      <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-ink px-4 py-2.5 text-sm font-semibold text-white">
        <Upload className="h-4 w-4" />{busy ? "Importing…" : "Choose .zip files"}
        <input type="file" accept=".zip,application/zip" multiple className="hidden" disabled={busy} data-testid="learn-import-input"
          onChange={(e) => { const fs = [...e.target.files]; e.target.value = ""; if (fs.length) run(fs); }} />
      </label>
      {rows.map((r) => (
        <div key={r.file} className="mt-5" data-testid={`learn-import-result-${r.file}`}>
          <p className="text-sm font-semibold text-ink">{r.file}{r.summary && <span className="font-normal text-muted"> — {r.summary.created} created · {r.summary.updated} updated · {r.summary.errors} errors · files {r.assets.created + r.assets.updated} stored, {r.assets.unchanged} unchanged{!r.manifest_found && " · no manifest (files only)"}</span>}</p>
          {r.fatal && <p className="text-sm text-red-600">{r.fatal}</p>}
          {r.items.length > 0 && (
            <table className="mt-2 w-full text-xs" data-testid="learn-import-table">
              <tbody className="divide-y divide-border align-top">
                {r.items.map((it, i) => (
                  <tr key={i}><td className="py-1.5 pr-3 font-mono">{it.external_id || "—"}</td><td className="pr-3">{it.type}</td><td className="pr-3">{it.title}</td>
                    <td className={`pr-3 font-semibold ${it.action === "error" ? "text-red-600" : "text-green-700"}`}>{it.action}</td>
                    <td className="text-muted">{[...(it.errors || []), ...(it.warnings || [])].join("; ")}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}

function YouTubeBox({ settings, onDone }) {
  const [cid, setCid] = useState(settings.youtube_channel_id || "");
  const [busy, setBusy] = useState(false);
  const save = async () => { await api.put("/admin/learn/settings", { youtube_channel_id: cid }); toast.success("Saved"); onDone(); };
  const sync = async () => {
    setBusy(true);
    try { const r = (await api.post("/admin/learn/youtube-sync")).data; toast[r.status === "ok" ? "success" : "error"](`YouTube: ${r.status}, ${r.matched} linked${r.error ? ` — ${r.error}` : ""}`); } catch { toast.error("Sync failed"); }
    setBusy(false); onDone();
  };
  return (
    <div className="rounded-lg border border-border bg-background p-5" data-testid="learn-youtube">
      <h3 className="font-heading font-bold text-ink">YouTube auto-link</h3>
      <p className="mt-1 text-sm text-muted">Every 6 h the channel feed is checked; a Short is linked when its title matches an episode or its description has "EP 01".</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="UC…" className="max-w-xs" data-testid="learn-yt-channel" />
        <Button onClick={save} className="bg-ink text-white" data-testid="learn-yt-save">Save</Button>
        <Button onClick={sync} variant="outline" disabled={busy} data-testid="learn-yt-sync"><RefreshCw className={`mr-1.5 h-4 w-4 ${busy ? "animate-spin" : ""}`} />Sync now</Button>
      </div>
      <p className="mt-2 text-xs text-muted" data-testid="learn-yt-last">Last run: {settings.last_yt_at ? new Date(settings.last_yt_at).toLocaleString("en-IN") : "never"} · {settings.last_yt_status} · {settings.last_yt_matched ?? 0} linked{settings.last_yt_error ? ` · ${settings.last_yt_error}` : ""}</p>
    </div>
  );
}

function ItemRow({ it, onDone }) {
  const [pa, setPa] = useState(toLocalIST(it.publish_at));
  const patch = async (body) => { try { await api.patch(`/admin/learn/items/${it.id}`, body); onDone(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  return (
    <tr data-testid={`learn-admin-row-${it.external_id}`}>
      <td className="px-3 py-2 font-mono text-xs">{it.external_id}</td>
      <td className="px-3 py-2 text-xs">{it.type}</td>
      <td className="px-3 py-2"><a href={it.path} target="_blank" rel="noreferrer" className="font-medium text-ink hover:text-accent">{it.title}</a></td>
      <td className={`px-3 py-2 text-xs font-semibold ${STATUS_CLS[it.status]}`} data-testid={`learn-status-${it.external_id}`}>{it.status}</td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <input type="datetime-local" value={pa} onChange={(e) => setPa(e.target.value)} className="rounded border border-border bg-background px-1.5 py-1 text-xs" data-testid={`learn-publish-${it.external_id}`} />
          {pa !== toLocalIST(it.publish_at) && <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => patch({ publish_at: `${pa}:00+05:30` })} data-testid={`learn-publish-save-${it.external_id}`}>Save</Button>}
        </div>
      </td>
      <td className="px-3 py-2 text-xs">{it.type === "episode" ? (it.youtube_id ? `yes (${it.youtube_source || "?"})` : "no") : "—"}</td>
      <td className="px-3 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => patch({ hidden: !it.hidden })} data-testid={`learn-toggle-${it.external_id}`}>{it.hidden ? "Unhide" : "Hide"}</Button></td>
    </tr>
  );
}

export function LearnAdmin() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-learn"], queryFn: async () => (await api.get("/admin/learn")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-learn"] });
  if (!data) return null;
  return (
    <div className="space-y-6 pt-4" data-testid="learn-admin">
      <div>
        <h2 className="font-heading text-xl font-bold text-ink">Learn — @bapnadigital</h2>
        <p className="mt-1 text-sm text-muted">Items go live by themselves at publish_at (IST). Times below are IST.</p>
      </div>
      <ImportBox onDone={refresh} />
      <YouTubeBox settings={data.settings} onDone={refresh} />
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm" data-testid="learn-items-table">
          <thead className="bg-card text-left text-xs uppercase tracking-wider text-muted">
            <tr><th className="px-3 py-3">ID</th><th className="px-3 py-3">Type</th><th className="px-3 py-3">Title</th><th className="px-3 py-3">Status</th><th className="px-3 py-3">Publish at (IST)</th><th className="px-3 py-3">YouTube</th><th className="px-3 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.items.map((it) => <ItemRow key={`${it.id}-${it.publish_at}`} it={it} onDone={refresh} />)}
            {data.items.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted">No items yet — import a bundle.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
